import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type {
  LayoutRect,
  PdfRasterFallback,
  ViewerPosition,
} from '../contracts/models';
import {
  pickSafeRenderScale,
  shouldUsePdfRasterFallback,
} from '../pdf/renderBudget';
import {
  clampPdfViewportScale,
  zoomViewportAtPoint,
} from '../geometry/viewportScale';
import {
  type PdfDocumentProxy,
  type PdfPageProxy,
  type PdfRenderTask,
} from '../pdf/runtime';
import { useViewerPdfDocument } from '../pdf/queries';
import {
  calculatePdfFit,
  clientToPdfPoint,
  getPdfDisplayDimensions,
  type PdfViewportDimensions,
} from '../pdf/viewport';

const DESIRED_RENDER_SCALE = 2;

export interface PdfPageViewportProps {
  fileUrl: string;
  pageNumber?: number;
  rotation?: number;
  cropRegion?: LayoutRect | null;
  rasterFallback?: PdfRasterFallback;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
  scale: number;
  position: ViewerPosition;
  onScaleChange(scale: number): void;
  onPositionChange(position: ViewerPosition): void;
  onLoadSuccess?(pageCount: number): void;
  onDimensionsLoad?(dimensions: PdfViewportDimensions): void;
  onFitComplete?(
    scale: number,
    position: ViewerPosition,
  ): void;
  onInitialRenderComplete?(): void;
  onInitialRenderError?(): void;
  onBackgroundClick?(x: number, y: number): void;
  loadingMessage?: string;
  loadDocument?: (
    fileUrl: string,
  ) => Promise<PdfDocumentProxy>;
  children?:
    | ReactNode
    | ((dimensions: PdfViewportDimensions) => ReactNode);
}

export interface PdfPageTransforms {
  stage: string;
  asset: string | undefined;
}

export const isPdfInitialViewReady = (
  contentKey: string,
  renderedContentKey: string,
  fittedContentKey: string,
): boolean =>
  renderedContentKey === contentKey &&
  fittedContentKey === contentKey;

export const shouldStartPdfPan = (
  target: EventTarget | null,
): boolean => {
  const element = target as
    | { closest?: (selector: string) => Element | null }
    | null;
  return !element?.closest?.('[data-layout-overlay]');
};

export const getPdfPageTransforms = (
  position: ViewerPosition,
  scale: number,
  dimensions: PdfViewportDimensions | null,
  flippedHorizontal: boolean,
  flippedVertical: boolean,
): PdfPageTransforms => {
  const assetTransforms = [
    flippedHorizontal && dimensions
      ? `translate(${dimensions.width}px, 0)`
      : '',
    flippedVertical && dimensions
      ? `translate(0, ${dimensions.height}px)`
      : '',
    flippedHorizontal ? 'scaleX(-1)' : '',
    flippedVertical ? 'scaleY(-1)' : '',
  ].filter(Boolean);

  return {
    stage: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    asset:
      assetTransforms.length > 0
        ? assetTransforms.join(' ')
        : undefined,
  };
};

export const PdfPageViewport = ({
  fileUrl,
  pageNumber = 1,
  rotation = 0,
  cropRegion = null,
  rasterFallback,
  flippedHorizontal = false,
  flippedVertical = false,
  scale,
  position,
  onScaleChange,
  onPositionChange,
  onLoadSuccess,
  onDimensionsLoad,
  onFitComplete,
  onInitialRenderComplete,
  onInitialRenderError,
  onBackgroundClick,
  loadingMessage = 'Loading Layout…',
  loadDocument,
  children,
}: PdfPageViewportProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<PdfRenderTask | null>(null);
  const dragAnchorRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const mouseDownRef = useRef<ViewerPosition | null>(null);
  const draggedRef = useRef(false);
  const touchDistanceRef = useRef<number | null>(null);
  const touchScaleRef = useRef(scale);
  const touchAnchorRef = useRef<ViewerPosition | null>(null);
  const liveScaleRef = useRef(scale);
  const livePositionRef = useRef(position);
  const fittedKeyRef = useRef('');
  const renderedContentKeyRef = useRef('');
  const fittedContentKeyRef = useRef('');
  const readyContentKeyRef = useRef('');
  const readyFrameRef = useRef<number | null>(null);
  const renderCallbacksRef = useRef({
    onLoadSuccess,
    onDimensionsLoad,
    onInitialRenderComplete,
    onInitialRenderError,
  });
  renderCallbacksRef.current = {
    onLoadSuccess,
    onDimensionsLoad,
    onInitialRenderComplete,
    onInitialRenderError,
  };
  const stableCropRegion = useMemo<LayoutRect | null>(
    () =>
      cropRegion
        ? [
            cropRegion[0],
            cropRegion[1],
            cropRegion[2],
            cropRegion[3],
          ]
        : null,
    [
      cropRegion?.[0],
      cropRegion?.[1],
      cropRegion?.[2],
      cropRegion?.[3],
    ],
  );
  const [rasterFailed, setRasterFailed] = useState(false);
  const useRasterFallback = Boolean(
    rasterFallback &&
      shouldUsePdfRasterFallback() &&
      !rasterFailed,
  );
  const activeAssetUrl = useRasterFallback
    ? rasterFallback?.url
    : fileUrl;
  const contentKey = `${activeAssetUrl}:${pageNumber}:${rotation}:${stableCropRegion?.join(',') ?? ''}`;
  const [dimensions, setDimensions] =
    useState<PdfViewportDimensions | null>(null);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const documentQuery = useViewerPdfDocument(
    useRasterFallback ? null : fileUrl,
    loadDocument,
  );
  const pdfDocument = documentQuery.data ?? null;

  const scheduleInitialReady = useCallback(
    (key: string) => {
      if (
        readyContentKeyRef.current === key ||
        !isPdfInitialViewReady(
          key,
          renderedContentKeyRef.current,
          fittedContentKeyRef.current,
        )
      ) {
        return;
      }
      if (readyFrameRef.current !== null) {
        window.cancelAnimationFrame(readyFrameRef.current);
      }
      readyFrameRef.current = window.requestAnimationFrame(() => {
        readyFrameRef.current = window.requestAnimationFrame(() => {
          readyFrameRef.current = null;
          if (
            readyContentKeyRef.current !== key &&
            isPdfInitialViewReady(
              key,
              renderedContentKeyRef.current,
              fittedContentKeyRef.current,
            )
          ) {
            readyContentKeyRef.current = key;
            renderCallbacksRef.current.onInitialRenderComplete?.();
          }
        });
      });
    },
    [],
  );

  useEffect(() => {
    liveScaleRef.current = scale;
    livePositionRef.current = position;
  }, [position, scale]);

  useEffect(() => {
    setRasterFailed(false);
  }, [rasterFallback?.url]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setDimensions(null);
    renderedContentKeyRef.current = '';
    fittedContentKeyRef.current = '';
  }, [contentKey]);

  useEffect(() => {
    if (!useRasterFallback || !rasterFallback) return;
    const rasterDimensions = {
      width: rasterFallback.width,
      height: rasterFallback.height,
    };
    setDimensions(rasterDimensions);
    setRendering(false);
    renderCallbacksRef.current.onDimensionsLoad?.(
      rasterDimensions,
    );
    if (canvasRef.current) {
      canvasRef.current.width = 0;
      canvasRef.current.height = 0;
    }
  }, [
    rasterFallback?.height,
    rasterFallback?.width,
    useRasterFallback,
  ]);

  useEffect(
    () => () => {
      if (readyFrameRef.current !== null) {
        window.cancelAnimationFrame(readyFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (pdfDocument) {
      renderCallbacksRef.current.onLoadSuccess?.(
        pdfDocument.numPages,
      );
    }
  }, [pdfDocument]);

  useEffect(() => {
    if (useRasterFallback || !documentQuery.error) return;
    setError(documentQuery.error.message);
    setLoading(false);
    renderCallbacksRef.current.onInitialRenderError?.();
  }, [documentQuery.error, useRasterFallback]);

  useEffect(() => {
    if (useRasterFallback || !pdfDocument || !canvasRef.current) return;
    let cancelled = false;
    let page: PdfPageProxy | null = null;

    const render = async () => {
      try {
        setRendering(true);
        setError(null);
        page = await pdfDocument.getPage(pageNumber);
        if (cancelled || !canvasRef.current) return;
        const naturalViewport = page.getViewport({
          scale: 1,
          rotation,
        });
        const displayDimensions = getPdfDisplayDimensions(
          naturalViewport,
          stableCropRegion,
        );
        setDimensions(displayDimensions);
        renderCallbacksRef.current.onDimensionsLoad?.(
          displayDimensions,
        );

        const renderScale = pickSafeRenderScale(
          naturalViewport.width,
          naturalViewport.height,
          DESIRED_RENDER_SCALE,
        );
        const renderViewport = page.getViewport({
          scale: renderScale,
          rotation,
        });
        const canvas = canvasRef.current;
        canvas.width = Math.round(
          displayDimensions.width * renderScale,
        );
        canvas.height = Math.round(
          displayDimensions.height * renderScale,
        );
        canvas.style.width = `${displayDimensions.width}px`;
        canvas.style.height = `${displayDimensions.height}px`;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Could not render PDF canvas.');
        if (stableCropRegion) {
          context.translate(
            -stableCropRegion[0] * renderScale,
            -stableCropRegion[1] * renderScale,
          );
        }
        renderTaskRef.current?.cancel();
        const task = page.render({
          canvasContext: context,
          viewport: renderViewport,
        });
        renderTaskRef.current = task;
        await task.promise;
        if (cancelled) return;
        renderTaskRef.current = null;
        setLoading(false);
        setRendering(false);
        renderedContentKeyRef.current = contentKey;
        scheduleInitialReady(contentKey);
      } catch (renderError: unknown) {
        const typed = renderError as {
          name?: string;
          message?: string;
        };
        if (typed.name === 'RenderingCancelledException') return;
        if (!cancelled) {
          setError(typed.message ?? 'Failed to render PDF.');
          setLoading(false);
          setRendering(false);
          renderCallbacksRef.current.onInitialRenderError?.();
        }
      } finally {
        page?.cleanup?.();
      }
    };
    void render();
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      page?.cleanup?.();
    };
  }, [
    contentKey,
    pdfDocument,
    pageNumber,
    rotation,
    scheduleInitialReady,
    stableCropRegion,
    useRasterFallback,
  ]);

  const handleRasterLoad = useCallback(() => {
    setLoading(false);
    setRendering(false);
    renderedContentKeyRef.current = contentKey;
    scheduleInitialReady(contentKey);
  }, [contentKey, scheduleInitialReady]);

  const handleRasterError = useCallback(() => {
    // Fall back to client-side PDF.js rendering when the host raster cannot
    // be loaded (for example, an expired session or transient server error).
    setRasterFailed(true);
    setDimensions(null);
    setLoading(true);
    renderedContentKeyRef.current = '';
    fittedContentKeyRef.current = '';
  }, []);

  useEffect(() => {
    if (
      !dimensions ||
      !containerRef.current ||
      fittedKeyRef.current === contentKey
    ) {
      return;
    }
    const fit = calculatePdfFit(
      {
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      },
      dimensions,
    );
    fittedKeyRef.current = contentKey;
    onScaleChange(fit.scale);
    onPositionChange(fit.position);
    onFitComplete?.(fit.scale, fit.position);
    fittedContentKeyRef.current = contentKey;
    scheduleInitialReady(contentKey);
  }, [
    contentKey,
    dimensions,
    onFitComplete,
    onPositionChange,
    onScaleChange,
    scheduleInitialReady,
    stableCropRegion,
  ]);

  const toPdfPoint = useCallback(
    (clientX: number, clientY: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return clientToPdfPoint(
        { x: clientX, y: clientY },
        { x: rect.left, y: rect.top },
        livePositionRef.current,
        liveScaleRef.current,
      );
    },
    [],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = container.getBoundingClientRect();
      const mouse = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const currentScale = liveScaleRef.current;
      const currentPosition = livePositionRef.current;
      const nextScale = clampPdfViewportScale(
        currentScale * (event.deltaY > 0 ? 0.9 : 1.1),
      );
      const transform = zoomViewportAtPoint(
        currentPosition,
        currentScale,
        nextScale,
        mouse,
      );
      onPositionChange(transform.position);
      onScaleChange(transform.scale);
    };
    container.addEventListener('wheel', handleWheel, {
      passive: false,
    });
    return () =>
      container.removeEventListener('wheel', handleWheel);
  }, [onPositionChange, onScaleChange]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) return;
      if (!shouldStartPdfPan(event.target)) return;
      event.preventDefault();
      draggedRef.current = false;
      mouseDownRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      dragAnchorRef.current = {
        x: event.clientX - livePositionRef.current.x,
        y: event.clientY - livePositionRef.current.y,
      };
    },
    [],
  );

  useEffect(() => {
    const handleMove = (event: MouseEvent) => {
      const anchor = dragAnchorRef.current;
      if (!anchor) return;
      const down = mouseDownRef.current;
      if (
        down &&
        Math.hypot(
          event.clientX - down.x,
          event.clientY - down.y,
        ) > 2
      ) {
        draggedRef.current = true;
      }
      onPositionChange({
        x: event.clientX - anchor.x,
        y: event.clientY - anchor.y,
      });
    };
    const handleUp = (event: MouseEvent) => {
      if (!dragAnchorRef.current) return;
      const wasDragged = draggedRef.current;
      dragAnchorRef.current = null;
      mouseDownRef.current = null;
      if (!wasDragged && onBackgroundClick) {
        const point = toPdfPoint(event.clientX, event.clientY);
        if (point) onBackgroundClick(point.x, point.y);
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [onBackgroundClick, onPositionChange, toPdfPoint]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let touchDragged = false;
    let touchStart: ViewerPosition | null = null;
    const handleStart = (event: TouchEvent) => {
      if (!container.contains(event.target as Node)) return;
      touchDragged = false;
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        touchStart = { x: touch.clientX, y: touch.clientY };
        dragAnchorRef.current = {
          x: touch.clientX - livePositionRef.current.x,
          y: touch.clientY - livePositionRef.current.y,
        };
      } else if (event.touches.length === 2) {
        event.preventDefault();
        const [first, second] = [
          event.touches[0],
          event.touches[1],
        ];
        touchDistanceRef.current = Math.hypot(
          second.clientX - first.clientX,
          second.clientY - first.clientY,
        );
        touchScaleRef.current = liveScaleRef.current;
        const rect = container.getBoundingClientRect();
        const midpoint = {
          x: (first.clientX + second.clientX) / 2 - rect.left,
          y: (first.clientY + second.clientY) / 2 - rect.top,
        };
        touchAnchorRef.current = {
          x:
            (midpoint.x - livePositionRef.current.x) /
            liveScaleRef.current,
          y:
            (midpoint.y - livePositionRef.current.y) /
            liveScaleRef.current,
        };
        dragAnchorRef.current = null;
      }
    };
    const handleMove = (event: TouchEvent) => {
      if (event.touches.length === 1 && dragAnchorRef.current) {
        event.preventDefault();
        touchDragged = true;
        const touch = event.touches[0];
        onPositionChange({
          x: touch.clientX - dragAnchorRef.current.x,
          y: touch.clientY - dragAnchorRef.current.y,
        });
      } else if (
        event.touches.length === 2 &&
        touchDistanceRef.current &&
        touchAnchorRef.current
      ) {
        event.preventDefault();
        touchDragged = true;
        const first = event.touches[0];
        const second = event.touches[1];
        const distance = Math.hypot(
          second.clientX - first.clientX,
          second.clientY - first.clientY,
        );
        const nextScale = Math.max(
          0.05,
          Math.min(
            20,
            touchScaleRef.current *
              (distance / touchDistanceRef.current),
          ),
        );
        const rect = container.getBoundingClientRect();
        const midpoint = {
          x: (first.clientX + second.clientX) / 2 - rect.left,
          y: (first.clientY + second.clientY) / 2 - rect.top,
        };
        onPositionChange({
          x: midpoint.x - touchAnchorRef.current.x * nextScale,
          y: midpoint.y - touchAnchorRef.current.y * nextScale,
        });
        onScaleChange(nextScale);
      }
    };
    const handleEnd = (event: TouchEvent) => {
      if (
        !touchDragged &&
        touchStart &&
        event.changedTouches[0] &&
        onBackgroundClick
      ) {
        const touch = event.changedTouches[0];
        const point = toPdfPoint(touch.clientX, touch.clientY);
        if (point) onBackgroundClick(point.x, point.y);
      }
      dragAnchorRef.current = null;
      touchDistanceRef.current = null;
      touchAnchorRef.current = null;
      touchStart = null;
    };
    container.addEventListener('touchstart', handleStart, {
      passive: false,
    });
    container.addEventListener('touchmove', handleMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleEnd);
    container.addEventListener('touchcancel', handleEnd);
    return () => {
      container.removeEventListener('touchstart', handleStart);
      container.removeEventListener('touchmove', handleMove);
      container.removeEventListener('touchend', handleEnd);
      container.removeEventListener('touchcancel', handleEnd);
    };
  }, [
    onBackgroundClick,
    onPositionChange,
    onScaleChange,
    toPdfPoint,
  ]);

  useEffect(
    () => () => {
      if (canvasRef.current) {
        canvasRef.current.width = 0;
        canvasRef.current.height = 0;
      }
    },
    [],
  );

  const transforms = getPdfPageTransforms(
    position,
    scale,
    dimensions,
    flippedHorizontal,
    flippedVertical,
  );

  return (
    <div
      ref={containerRef}
      className="iv-pdf-page-viewport"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: dragAnchorRef.current ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
    >
      {(loading || rendering) && !error ? (
        <div className="iv-pdf-loading" role="status">
          {loadingMessage}
        </div>
      ) : null}
      {error ? (
        <div className="iv-pdf-error" role="alert">
          {error}
        </div>
      ) : null}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: dimensions?.width,
          height: dimensions?.height,
          transformOrigin: '0 0',
          transform: transforms.stage,
          background: 'white',
          boxShadow: dimensions
            ? '0 4px 24px rgba(0,0,0,0.18)'
            : 'none',
        }}
      >
        <div
          style={{
            width: dimensions?.width,
            height: dimensions?.height,
            transformOrigin: '0 0',
            transform: transforms.asset,
          }}
        >
          {useRasterFallback && rasterFallback ? (
            <img
              src={rasterFallback.url}
              alt=""
              width={rasterFallback.width}
              height={rasterFallback.height}
              onLoad={handleRasterLoad}
              onError={handleRasterError}
              draggable={false}
              style={{
                display: 'block',
                width: `${rasterFallback.width}px`,
                height: `${rasterFallback.height}px`,
              }}
            />
          ) : (
            <canvas
              ref={canvasRef}
              width={0}
              height={0}
              style={{ display: 'block' }}
            />
          )}
        </div>
        {dimensions
          ? typeof children === 'function'
            ? children(dimensions)
            : children
          : null}
      </div>
    </div>
  );
};
